import { ISelctedSeat } from "@/redux/rent/types";
import dateFormat from "dateformat";

const COURT_ID_PADLE_1 = [
  'c5230dbe-bde4-11ef-80cd-ac1f6bea5f2d',
  'c5230dd1-bde4-11ef-80cd-ac1f6bea5f2d',
  'cd33359d-bde4-11ef-80cd-ac1f6bea5f2d',
];

const COURT_ID_PADLE_2 = [
  '2336b85a-8400-11f0-80d2-ac1f6bea5f2d',
  '2336b86d-8400-11f0-80d2-ac1f6bea5f2d',
  '2b356290-8400-11f0-80d2-ac1f6bea5f2d',
];

const COURT_ID_PADLE = [...COURT_ID_PADLE_1, ...COURT_ID_PADLE_2];

const COURT_ID_HARD2 = [
  '39df27c8-eb55-11e9-9ab5-382c4a64b65a',
  '4f0b42a8-eb55-11e9-9ab5-382c4a64b65a',
  '58234368-eb55-11e9-9ab5-382c4a64b65a',
  '05e09479-574c-11ec-80d6-ac1f6bb3f7d2',
];

export type DiscountType = 'PADEL' | 'HARD';

export interface Discount {
  type: DiscountType;
  price: number;
  slots: string[];
}

export interface BasketItem {
  courtId: string;
  date: string;
  time: string;
  price: number;
  originalPrice?: number;
  hasDiscount?: boolean;
  discountType?: DiscountType;
  key: string;
}

function isWeekend(date: string): boolean {
  const d = new Date(date);
  const dayOfWeek = d.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
}

function isTimeInRange(time: string, startHour: number, endHour: number): boolean {
  const [hours] = time.split(':').map(Number);
  return hours >= startHour && hours < endHour;
}

function getHour(time: string): number {
  return parseInt(time.split(':')[0], 10);
}

type OccupiedByOthersFn = (courtId: string, date: string, time: string) => boolean;

function findPadelDiscount(
  basket: BasketItem[],
  date: string,
  occupiedByOthersFn: OccupiedByOthersFn
): Discount | null {
  const isWeekendDay = isWeekend(date);
  
  const startHour = isWeekendDay ? 10 : 21;
  const endHour = isWeekendDay ? 18 : 23;
  
  const padelSlots = basket.filter((item) => {
    const isPadelCourt = COURT_ID_PADLE.includes(item.courtId);
    const isInTimeRange = isTimeInRange(item.time, startHour, endHour);
    const isNotOccupied = !occupiedByOthersFn(item.courtId, item.date, item.time);
    
    return isPadelCourt && isInTimeRange && isNotOccupied;
  });

  if (padelSlots.length === 0) {
    return null;
  }

  const slotsByHour = new Map<number, BasketItem[]>();
  padelSlots.forEach((slot) => {
    const hour = getHour(slot.time);
    if (!slotsByHour.has(hour)) {
      slotsByHour.set(hour, []);
    }
    slotsByHour.get(hour)!.push(slot);
  });

  const groups = [COURT_ID_PADLE_1, COURT_ID_PADLE_2];
  
  for (const group of groups) {
    for (let hour = startHour; hour < endHour - 1; hour++) {
      const hour1Slots = slotsByHour.get(hour) || [];
      const hour2Slots = slotsByHour.get(hour + 1) || [];
      
      const hour1GroupSlots = hour1Slots.filter(s => group.includes(s.courtId));
      const hour2GroupSlots = hour2Slots.filter(s => group.includes(s.courtId));
      
      const hour1Courts = new Set(hour1GroupSlots.map(s => s.courtId));
      const hour2Courts = new Set(hour2GroupSlots.map(s => s.courtId));
      
      if (hour1Courts.size >= 3 && hour2Courts.size >= 3) {
        const commonCourts = Array.from(hour1Courts).filter(c => hour2Courts.has(c));
        
        if (commonCourts.length >= 3) {
          const selectedCourts = commonCourts.slice(0, 3);
          
          const discountSlots: string[] = [];
          selectedCourts.forEach((courtId) => {
            const hour1Slot = hour1GroupSlots.find(s => s.courtId === courtId);
            const hour2Slot = hour2GroupSlots.find(s => s.courtId === courtId);
            
            if (hour1Slot) discountSlots.push(hour1Slot.key);
            if (hour2Slot) discountSlots.push(hour2Slot.key);
          });
          
          if (discountSlots.length >= 6) {
            return {
              type: 'PADEL',
              price: 3000,
              slots: discountSlots,
            };
          }
        }
      }
    }
  }

  return null;
}

function findHardDiscount(
  basket: BasketItem[],
  date: string,
  occupiedByOthersFn: OccupiedByOthersFn
): Discount | null {
  if (!isWeekend(date)) {
    return null;
  }

  const hardSlots = basket.filter((item) => {
    const isHardCourt = COURT_ID_HARD2.includes(item.courtId);
    const isInTimeRange = isTimeInRange(item.time, 14, 18);
    const isNotOccupied = !occupiedByOthersFn(item.courtId, item.date, item.time);
    
    return isHardCourt && isInTimeRange && isNotOccupied;
  });

  if (hardSlots.length === 0) {
    return null;
  }

  const slotsByCourt = new Map<string, BasketItem[]>();
  hardSlots.forEach((slot) => {
    if (!slotsByCourt.has(slot.courtId)) {
      slotsByCourt.set(slot.courtId, []);
    }
    slotsByCourt.get(slot.courtId)!.push(slot);
  });

  const totalSlotsPerCourt = 4;
  
  let totalInBasket = 0;
  let totalOccupied = 0;
  
  COURT_ID_HARD2.forEach((courtId) => {
    const basketSlots = slotsByCourt.get(courtId) || [];
    totalInBasket += basketSlots.length;
    
    for (let hour = 14; hour < 18; hour++) {
      const time = `${hour.toString().padStart(2, '0')}:00`;
      if (occupiedByOthersFn(courtId, date, time)) {
        totalOccupied++;
      }
    }
  });

  const totalAvailable = COURT_ID_HARD2.length * totalSlotsPerCourt;
  const condition = totalAvailable - (totalInBasket + totalOccupied) <= 2;

  if (condition && totalInBasket > 0) {
    const discountSlots = hardSlots.map(slot => slot.key);
    
    return {
      type: 'HARD',
      price: 3000,
      slots: discountSlots,
    };
  }

  return null;
}

export function calculateBestDiscount(
  basket: { [key: string]: ISelctedSeat },
  date: string,
  occupiedByOthersFn: OccupiedByOthersFn = () => false
): Discount | null {
  const basketItems: BasketItem[] = Object.keys(basket).map((key) => {
    const seat = basket[key];
    const courtId = seat.bitrixCourtId || seat.courtId?.toString() || '';
    return {
      courtId,
      date: seat.date,
      time: seat.time,
      price: seat.price,
      originalPrice: seat.originalPrice,
      hasDiscount: seat.hasDiscount,
      discountType: seat.discountType,
      key,
    };
  });

  const dayBasket = basketItems.filter(item => item.date === date);

  const hardDiscount = findHardDiscount(dayBasket, date, occupiedByOthersFn);
  if (hardDiscount) {
    return hardDiscount;
  }

  const padelDiscount = findPadelDiscount(dayBasket, date, occupiedByOthersFn);
  if (padelDiscount) {
    return padelDiscount;
  }

  return null;
}

export function recalculateDiscountsForDay(
  basket: { [key: string]: ISelctedSeat },
  targetDate: string,
  occupiedByOthersFn: OccupiedByOthersFn = () => false
): { [key: string]: ISelctedSeat } {
  const updatedBasket = { ...basket };

  const dayKeys = Object.keys(updatedBasket).filter(
    (key) => updatedBasket[key].date === targetDate
  );

  dayKeys.forEach((key) => {
    const seat = updatedBasket[key];
    const originalPrice = (seat as any).originalPrice || seat.price;
    
    updatedBasket[key] = {
      ...seat,
      price: originalPrice,
      is_special_price: false,
    };
    
    delete (updatedBasket[key] as any).hasDiscount;
    delete (updatedBasket[key] as any).discountType;
    delete (updatedBasket[key] as any).originalPrice;
  });

  const discount = calculateBestDiscount(updatedBasket, targetDate, occupiedByOthersFn);

  if (discount) {
    const totalSlots = discount.slots.length;
    const pricePerSlot = Math.round((discount.price / totalSlots) * 100) / 100;

    discount.slots.forEach((slotKey) => {
      if (updatedBasket[slotKey]) {
        const seat = updatedBasket[slotKey];
        const originalPrice = seat.price;

        updatedBasket[slotKey] = {
          ...seat,
          price: pricePerSlot,
          is_special_price: true,
          originalPrice: originalPrice,
          hasDiscount: true,
          discountType: discount.type,
        };
      }
    });
  }

  return updatedBasket;
}
